import React, { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { MantineProvider, createTheme } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { ModalsProvider } from '@mantine/modals'

// ── Error Boundary — จับ white screen errors แสดงข้อความแทน ──────────────────
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: any) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: 'Inter, sans-serif' }}>
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 24, maxWidth: 600 }}>
            <h3 style={{ color: '#dc2626', margin: '0 0 8px' }}>⚠️ เกิดข้อผิดพลาด</h3>
            <p style={{ color: '#374151', margin: '0 0 12px', fontSize: 14 }}>
              หน้านี้โหลดไม่ได้ กรุณา Refresh หรือกลับหน้าหลัก
            </p>
            <pre style={{ background: '#fff', padding: 12, borderRadius: 8, fontSize: 11, color: '#6b7280', overflow: 'auto', margin: '0 0 16px' }}>
              {this.state.error.message}
            </pre>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => window.location.reload()}
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>
                🔄 Refresh
              </button>
              <button onClick={() => { this.setState({ error: null }); window.location.href = '/' }}
                style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>
                🏠 กลับหน้าหลัก
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
import { AuthProvider, useAuth } from './hooks/useAuth'
import { PlatformAuthProvider, usePlatformAuth } from './hooks/usePlatformAuth'
import PlatformLoginPage from './pages/platform/PlatformLoginPage'
import PlatformChangePasswordPage from './pages/platform/PlatformChangePasswordPage'
import PlatformDashboardPage from './pages/platform/PlatformDashboardPage'
import PlatformSettingsPage from './pages/platform/PlatformSettingsPage'
import MarketingPage from './pages/MarketingPage'
import { TENANT_BASE_DOMAIN } from './api/platformClient'
import './i18n'
import './index.css'
import Layout from './components/layout/Layout'
import NativeLayout from './components/layout/NativeLayout'
import LoginPage from './pages/LoginPage'
import HandoverConfirmPage from './pages/HandoverConfirmPage'
import DashboardPage from './pages/DashboardPage'
import MobileDashboardPage from './pages/MobileDashboardPage'
import HomePage from './pages/HomePage'
import AccessLevelPage from './pages/AccessLevelPage'
import AssetsPage from './pages/AssetsPage'
import MaintenancePage from './pages/MaintenancePage'
import WriteOffPage from './pages/WriteOffPage'
import AssetCheckingPage from './pages/AssetCheckingPage'
import ReportsPage from './pages/ReportsPage'
import BorrowPage from './pages/BorrowPage'
import HandoverPage from './pages/HandoverPage'
import TransferPage from './pages/TransferPage'
import UsersPage from './pages/UsersPage'
import ApprovalsPage from './pages/ApprovalsPage'
import ScanPage from './pages/ScanPage'
import ProfilePage from './pages/ProfilePage'
import ConfigurationPage from './pages/ConfigurationPage'
import AuditLogPage from './pages/AuditLogPage'
import InventoryPage from './pages/InventoryPage'
import StockConfirmPage from './pages/StockConfirmPage'
import NotificationsPage from './pages/NotificationsPage'
import ChangeLogPage from './pages/ChangeLogPage'
import LicensePage from './pages/LicensePage'
import CompanyProfilePage from './pages/CompanyProfilePage'
import WorkOrdersPage from './pages/WorkOrdersPage'
import HkOrdersPage from './pages/HkOrdersPage'
import FacilityDashboardPage from './pages/FacilityDashboardPage'
import StockDashboardPage from './pages/StockDashboardPage'
import StockReportsPage from './pages/StockReportsPage'
import ReminderSettingsPage from './pages/ReminderSettingsPage'

import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/charts/styles.css'
import '@mantine/dates/styles.css'

// สี theme ที่ Super Admin เลือกให้แต่ละบริษัทได้ (คุมโทนสีของระบบ ทีมของ tenant แก้เองไม่ได้)
export const THEME_COLOR_PRESETS = ['navy', 'black', 'white', 'brown'] as const
export type ThemeColorPreset = (typeof THEME_COLOR_PRESETS)[number]

// ค่าจริงที่ inject เป็น CSS variable — ปุ่ม/badge หลักทั่วระบบอ่านค่าจากตัวแปรพวกนี้
// (ดู --brand-primary / --brand-on-primary / --brand-border ที่ใช้ทั่วแอป)
export const THEME_COLOR_VALUES: Record<ThemeColorPreset, { bg: string; text: string; border: string; swatch: string }> = {
  navy:  { bg: '#0a0f1c', text: '#ffffff', border: 'transparent', swatch: '#0a0f1c' },
  black: { bg: '#0a0a0a', text: '#ffffff', border: 'transparent', swatch: '#0a0a0a' },
  white: { bg: '#ffffff', text: '#0f172a', border: '#0f172a',     swatch: '#ffffff' },
  brown: { bg: '#3e2723', text: '#ffffff', border: 'transparent', swatch: '#3e2723' },
}

function buildTheme() {
  return createTheme({
    fontFamily: 'DM Sans, sans-serif',
    headings: { fontFamily: 'Inter, sans-serif' },
    // ── เปลี่ยนจาก teal เป็น assetosBlue ตามโลโก้แบรนด์ใหม่ (ไล่สีน้ำเงิน→เขียวมิ้นท์)
    // ใช้ค่าสีที่ sample จากไฟล์โลโก้จริงเป็นฐาน (#2563EB น้ำเงินหลัก) ไม่ใช่เดาเอาเอง —
    // มีผลกับปุ่ม/ลิงก์/component ที่ใช้ primaryColor ทั่วทั้งแอปนี้ (ไม่รวม Platform Console
    // ที่จงใจแยก theme ของตัวเองไว้ต่างหากเป็นดำ/ส้มตามที่ตกลงกันไว้) ──
    primaryColor: 'assetosBlue',
    defaultRadius: 'md',
    colors: {
      assetosBlue: ['#eef4ff','#d9e6ff','#b3ccff','#84acff','#5e90fa','#3b74f0','#2563eb','#1d4ed8','#1a3fad','#16337f'],
      // เก็บ teal ไว้ให้ component เดิมที่อ้างอิงสี 'teal' ตรงๆ ยังใช้งานได้ปกติ ไม่ต้องไล่แก้ทุกจุด
      teal: ['#f0fdf9','#ccfbf1','#99f6e4','#5eead4','#2dd4bf','#14b8a6','#0d9488','#0f766e','#115e59','#134e4a'],
    },
    components: {
      Button: {
        styles: {
          root: { fontFamily: 'DM Sans, sans-serif', fontWeight: 500, letterSpacing: '-0.1px' },
        },
      },
      TextInput: {
        styles: {
          label: { fontWeight: 500, fontSize: 13, color: '#374151', marginBottom: 4 },
          input: { borderColor: '#e5e7eb', fontSize: 14 },
        },
      },
      Select: {
        styles: {
          label: { fontWeight: 500, fontSize: 13, color: '#374151', marginBottom: 4 },
          input: { borderColor: '#e5e7eb', fontSize: 14 },
        },
      },
      Table: {
        styles: {
          th: { fontWeight: 500, fontSize: 12, color: '#6b7280', letterSpacing: '0.3px', textTransform: 'uppercase' },
          td: { fontSize: 13 },
        },
      },
      Badge: {
        styles: {
          root: { fontWeight: 500, letterSpacing: '0.2px' },
        },
      },
      Modal: {
        styles: {
          header: { borderBottom: '1px solid #f3f4f6', paddingBottom: 12 },
          title: { fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 16 },
        },
      },
    },
  })
}

// (SuperAdminRoute ตัดออกแล้ว — พร้อมกับหน้า Super Admin panel)

// ── ตัดสินใจว่าหน้า "/" ควรโชว์อะไร — ถ้าเป็นโดเมนหลัก (ไม่มี subdomain เลย เช่น
// assetsservice.com ตรงๆ) และยังไม่ login ให้โชว์หน้าขายแทน login form ปกติ — ถ้าเป็น tenant
// subdomain (เช่น acme.assetsservice.com) หรือ login แล้ว ให้เข้า flow เดิมตามปกติทุกประการ ──
function RootRoute() {
  const { token } = useAuth()
  const hasToken = token ?? localStorage.getItem('token')
  const isBareMarketingDomain = TENANT_BASE_DOMAIN && window.location.hostname === TENANT_BASE_DOMAIN
  if (isBareMarketingDomain && !hasToken) return <MarketingPage />
  return <Guard>{Capacitor.isNativePlatform() ? <NativeLayout /> : <Layout />}</Guard>
}

function Guard({ children }: { children: React.ReactNode }) {
  const { token } = useAuth()
  const hasToken = token ?? localStorage.getItem('token')
  if (hasToken) return <>{children}</>
  const intended = window.location.pathname + window.location.search
  return <Navigate to={`/login?redirect=${encodeURIComponent(intended)}`} replace />
}

// ── แยกจาก Guard เดิมโดยสิ้นเชิง — เช็ค platform_token คนละก้อนกับ token ของ tenant user ปกติ
// และบังคับไปหน้าเปลี่ยนรหัสผ่านก่อนเสมอถ้า mustChangePassword ยังเป็น true (กันข้ามหน้าตรงไป
// dashboard ทั้งที่ยังใช้รหัสผ่านชั่วคราวอยู่ — ต่อให้พิมพ์ URL ตรงๆ ก็เด้งกลับมาหน้านี้เสมอ) ──
function PlatformGuard({ children }: { children: React.ReactNode }) {
  const { token, mustChangePassword } = usePlatformAuth()
  const hasToken = token ?? localStorage.getItem('platform_token')
  if (!hasToken) return <Navigate to="/platform/login" replace />
  if (mustChangePassword && window.location.pathname !== '/platform/change-password') {
    return <Navigate to="/platform/change-password" replace />
  }
  return <>{children}</>
}

// ต้องอยู่ "ใต้" AuthProvider เพื่ออ่าน companyThemeColor ของบริษัทที่ login อยู่ได้
// แล้ว inject เป็น CSS variable ให้ปุ่ม/badge หลักทั่วแอปอ่านค่าไปใช้ (ดู THEME_COLOR_VALUES ด้านบน)
function ThemedShell({ children }: { children: React.ReactNode }) {
  const { companyThemeColor } = useAuth()
  const theme = buildTheme()

  useEffect(() => {
    const preset = (THEME_COLOR_PRESETS as readonly string[]).includes(companyThemeColor ?? '')
      ? (companyThemeColor as ThemeColorPreset)
      : 'navy'
    const { bg, text, border } = THEME_COLOR_VALUES[preset]
    const root = document.documentElement.style
    root.setProperty('--brand-primary', bg)
    root.setProperty('--brand-on-primary', text)
    root.setProperty('--brand-border', border)
  }, [companyThemeColor])

  return (
    <MantineProvider theme={theme}>
      <Notifications position="top-right" />
      <ModalsProvider>{children}</ModalsProvider>
    </MantineProvider>
  )
}

export default function App() {
  return (
    <AuthProvider>
    <PlatformAuthProvider>
      <ThemedShell>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/platform/login" element={<PlatformLoginPage />} />
            <Route path="/platform/change-password" element={<PlatformGuard><PlatformChangePasswordPage /></PlatformGuard>} />
            <Route path="/platform/dashboard" element={<PlatformGuard><PlatformDashboardPage /></PlatformGuard>} />
            <Route path="/platform/settings" element={<PlatformGuard><PlatformSettingsPage /></PlatformGuard>} />
            <Route path="/handover-confirm/:token" element={<HandoverConfirmPage />} />
            <Route path="/" element={<RootRoute />}>
              <Route index element={<HomePage />} />
              <Route path="dashboard" element={Capacitor.isNativePlatform() ? <MobileDashboardPage /> : <DashboardPage />} />
              <Route path="assets" element={<AssetsPage />} />
              <Route path="borrow" element={<BorrowPage />} />
              <Route path="handover" element={<HandoverPage />} />
              <Route path="transfer" element={<TransferPage />} />
              <Route path="maintenance" element={<MaintenancePage />} />
              <Route path="write-off" element={<WriteOffPage />} />
              <Route path="asset-checking" element={<AssetCheckingPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="approvals" element={<ApprovalsPage />} />
              <Route path="scan" element={<ScanPage />} />
              <Route path="configuration" element={<ConfigurationPage />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="stock-confirm" element={<StockConfirmPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="stock-dashboard" element={<StockDashboardPage />} />
              <Route path="stock-reports" element={<StockReportsPage />} />
              <Route path="reminder-settings" element={<ReminderSettingsPage />} />
              <Route path="changelog" element={<ChangeLogPage />} />
              <Route path="licenses" element={<LicensePage />} />
              <Route path="work-orders" element={<WorkOrdersPage />} />
              <Route path="hk-orders" element={<HkOrdersPage />} />
              <Route path="facility" element={<FacilityDashboardPage />} />
              <Route path="company-profile" element={<CompanyProfilePage />} />
              <Route path="access-levels" element={<AccessLevelPage />} />
              {/* super-admin route ตัดออกแล้ว — พร้อมกับหน้า Super Admin panel */}
            </Route>
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </ThemedShell>
    </PlatformAuthProvider>
    </AuthProvider>
  )
}
